import { useCallback } from 'react';
import { DocumentCategory, DocumentType, useCreateDocument, useCreateDocumentImage } from 'shared/data/walletsKyc/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks/useCompanyIdBasedOnUrl';

import { DocumentImage, KycDocument } from '../types';

export const useUploadKycDocument = () => {
    const { companyId } = useCompanyIdBasedOnUrl();
    const { mutateAsync: createDocument } = useCreateDocument();

    const { mutateAsync: createDocumentImage } = useCreateDocumentImage();

    const uploadDocument = useCallback(
        async (documentType: DocumentType, documentCategory: DocumentCategory, images: DocumentImage[]) =>
            images.some((image) => image.source)
                ? createDocument({
                      params: {
                          path: {
                              organizationId: companyId,
                          },
                      },
                      body: {
                          documentType,
                          documentCategory,
                      },
                  }).then(({ documentId }) =>
                      Promise.all(
                          images.map(({ id, imageSide, source }) =>
                              source
                                  ? createDocumentImage({
                                        params: {
                                            path: {
                                                organizationId: companyId,
                                                documentId,
                                            },
                                        },
                                        body: {
                                            imageSide,
                                            fileName: source.name,
                                            fileContent: source,
                                        },
                                    })
                                  : { documentId: id, imageSide }
                          )
                      )
                  )
                : images.map(({ id, ...image }) => ({ ...image, documentId: id })),
        [companyId, createDocument, createDocumentImage]
    );

    const handleUploadDocuments = useCallback(
        async (documentType: DocumentType, documentCategory: DocumentCategory, documents: KycDocument[]) =>
            Promise.all(
                documents.map(async (document) => uploadDocument(documentType, documentCategory, document.images))
            ).then((documents) => {
                const documentIds = new Set<string>();

                documents.flat().forEach((document) => {
                    documentIds.add(document.documentId);
                });

                return Array.from(documentIds);
            }),
        [uploadDocument]
    );

    return {
        onUploadDocuments: handleUploadDocuments,
    };
};
